import { useMemo, useState } from 'react';
import { AddButton, LiveScreen, QueryState, StatusPill, formatDate, formatNumber, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { WorkerDrawer, skillLabel, type WorkerRow } from '@/screens/parts/WorkerDrawer';
import { WORKER_TYPES, optionLabel } from '@/lib/practiceLists';

/**
 * Worker inventory (owner, 2026-09-21): everyone working on the farms, through a contractor or
 * engaged directly, with the safety facts an audit asks for - protective equipment, training,
 * and proof that no one is under 18.
 */
export default function WorkersScreen() {
  const { profile } = useAuth();
  const [stat, setStat] = useState<string | null>(null);
  const [open, setOpen] = useState<WorkerRow | 'new' | null>(null);
  const mayAdd = can(profile, 'party.register', 'create');
  const rows = useRows<WorkerRow>('workers', { orderBy: { column: 'full_name' } });

  const STATS = useMemo(() => [
    { key: 'all', label: 'Workers', match: () => true, tone: 'neutral' as const },
    { key: 'active', label: 'Active', tone: 'ok' as const, match: (row: WorkerRow) => row.is_active && !['suspended', 'terminated'].includes(row.status) },
    { key: 'contract', label: 'Through a contractor', tone: 'info' as const, match: (row: WorkerRow) => Boolean(row.contractor_id) },
    { key: 'direct', label: 'Engaged directly', tone: 'info' as const, match: (row: WorkerRow) => !row.contractor_id },
    { key: 'no_ppe', label: 'No PPE issued', tone: 'warn' as const, match: (row: WorkerRow) => row.is_active && !row.ppe_issued },
    { key: 'untrained', label: 'No safety training', tone: 'warn' as const, match: (row: WorkerRow) => row.is_active && !row.safety_trained_on },
    { key: 'women', label: 'Women', tone: 'info' as const, match: (row: WorkerRow) => row.gender === 'female' },
    { key: 'no_agreement', label: 'No agreed rate', tone: 'warn' as const, match: (row: WorkerRow) => row.is_active && row.active_agreements === 0 },
    { key: 'unconfirmed', label: 'Payment not confirmed', tone: 'error' as const, match: (row: WorkerRow) => row.unconfirmed_payments > 0 },
    { key: 'left', label: 'Left', tone: 'neutral' as const, match: (row: WorkerRow) => !row.is_active },
  ], []);
  const visible = useMemo(() => applyStat(rows.data ?? [], STATS, stat), [rows.data, STATS, stat]);

  const columns: Array<Column<WorkerRow>> = [
    { key: 'full_name', label: 'Worker', render: (row) => (
      <span className="cell-stack"><strong>{row.full_name}</strong><small className="muted">{row.worker_code}</small></span>
    ) },
    { key: 'contractor_name', label: 'Contractor', render: (row) => row.contractor_name ?? <span className="muted">Direct</span> },
    { key: 'worker_type', label: 'Type', render: (row) => optionLabel(WORKER_TYPES, row.worker_type) },
    { key: 'skill', label: 'Skill', render: (row) => skillLabel(row.skill) },
    { key: 'age_years', label: 'Age', align: 'right', render: (row) => row.age_years ?? '--' },
    { key: 'gender', label: 'Gender', render: (row) => (row.gender ? row.gender[0]!.toUpperCase() + row.gender.slice(1) : '--') },
    { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
    { key: 'joined_on', label: 'Joined', render: (row) => formatDate(row.joined_on) },
    { key: 'daily_wage', label: 'Daily wage', align: 'right', render: (row) => (row.daily_wage == null ? '--' : `₹${formatNumber(row.daily_wage, 0)}`) },
    { key: 'active_agreements', label: 'Agreed rates', align: 'right', render: (row) => row.active_agreements || <span className="field-error">None</span> },
    { key: 'total_paid', label: 'Paid', align: 'right', render: (row) => `₹${formatNumber(row.total_paid, 0)}` },
    { key: 'unconfirmed_payments', label: 'Unconfirmed', align: 'right', render: (row) => (row.unconfirmed_payments ? <span className="field-error">{row.unconfirmed_payments}</span> : '0') },
    { key: 'phone', label: 'Phone', render: (row) => row.phone ?? '--' },
    { key: 'ppe_issued', label: 'PPE', render: (row) => (row.ppe_issued ? 'Yes' : <span className="field-error">No</span>) },
    { key: 'safety_trained_on', label: 'Safety training', render: (row) => (row.safety_trained_on ? formatDate(row.safety_trained_on) : <span className="field-error">None</span>) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayAdd} label="Add worker" onClick={() => setOpen('new')}
                          title={mayAdd ? undefined : 'Your role cannot add workers.'} />}
      subtitle="Everyone working on the farms, with their contractor, safety equipment and training. No one under 18."
      title="Workers"
    >
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} secondary={{ sum: (row) => row.total_paid, unit: '', money: true }} stats={STATS} />
      <QueryState empty="No workers yet." query={rows}>
        <DataGrid columns={columns} exportName="workers" onRowClick={setOpen} rows={visible}
                  screenKey="party.register" searchPlaceholder="Name, code, contractor, village" />
      </QueryState>
      {open && <WorkerDrawer editing={open === 'new' ? null : open} key={open === 'new' ? 'new' : open.id} onClose={() => setOpen(null)} />}
    </LiveScreen>
  );
}
